import { checkPermissions, useAuth } from "@elit-hair/elit-tools";
import {
    Button,
    Icon,
    SearchBar,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
    transitions,
} from "@elit-hair/elit-ui";
import * as Sentry from "@sentry/react";
import { useQueries } from "@tanstack/react-query";
import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { TbEdit, TbPlus, TbTrash } from "react-icons/tb";
import { z } from "zod";

import { StaffModal } from "@/components/staff-modal";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { authSvcAxiosInstance } from "@/constants";
import { PERMISSIONS } from "@/constants/permissions";
import { useClinic } from "@/providers/hooks/use-clinic";
import { authServiceEndpoints } from "@/queries/auth.queries";
import { useClinics } from "@/queries/clinic.queries";
import { useCreateStaff, useDeleteStaff, useStaff, useUpdateStaff } from "@/queries/staff.queries";
import { useUser } from "@/queries/user.queries";
import type { Clinic } from "@/types/clinic.types";
import type { CreateStaffRequest, Staff } from "@/types/staff.types";
import { fadeInUp } from "@/utils/animations";

// Search params schema for staff route
const staffSearchSchema = z.object({
    search: z.string().optional(),
});

type StaffRowProps = {
    staff: Staff;
    clinicsData: Clinic[];
    onEdit: () => void;
    onDelete: () => void;
    isDeleting: boolean;
    canEdit?: boolean;
    canDelete?: boolean;
};

const StaffRow = ({
    staff,
    clinicsData,
    onEdit,
    onDelete,
    isDeleting,
    canEdit = true,
    canDelete = true,
}: StaffRowProps) => {
    const { t } = useTranslation();
    const { data: userData, isLoading: isLoadingUser } = useUser(staff.userId);

    return (
        <TableRow>
            <TableCell className="font-medium">
                {isLoadingUser ? (
                    <span className="text-muted-foreground">{t("loading")}...</span>
                ) : (
                    userData?.name || staff.userId
                )}
            </TableCell>
            <TableCell>{staff.userId}</TableCell>
            <TableCell>
                {staff.clinicIds && staff.clinicIds.length > 0
                    ? staff.clinicIds
                          .map((clinicId) => {
                              const clinic = clinicsData.find((c) => c.id === clinicId);
                              return clinic?.name || clinicId;
                          })
                          .join(", ")
                    : "-"}
            </TableCell>
            <TableCell>
                {(canEdit || canDelete) && (
                    <div className="flex gap-2">
                        {canEdit && (
                            <Button variant="outline" size="icon" onClick={onEdit}>
                                <Icon icon={TbEdit} />
                            </Button>
                        )}
                        {canDelete && (
                            <Button variant="outline" size="icon" onClick={onDelete} disabled={isDeleting}>
                                <Icon icon={TbTrash} />
                            </Button>
                        )}
                    </div>
                )}
            </TableCell>
        </TableRow>
    );
};

function StaffPage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { selectedClinic } = useClinic();
    const { userData } = useAuth();

    // Get search params from URL
    const { search: searchQuery } = useSearch({ from: "/staff" });
    // Local state for input value (for immediate UI updates)
    const [inputValue, setInputValue] = useState<string>(searchQuery || "");
    const [isStaffModalOpen, setIsStaffModalOpen] = useState<boolean>(false);
    const [editingStaff, setEditingStaff] = useState<Staff | null>(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
    const [staffToDelete, setStaffToDelete] = useState<string | null>(null);

    const canCreate = checkPermissions(userData?.permissionCodes, [PERMISSIONS.TREATMENT_STAFF_CREATE]);
    const canEdit = checkPermissions(userData?.permissionCodes, [PERMISSIONS.TREATMENT_STAFF_UPDATE]);
    const canDelete = checkPermissions(userData?.permissionCodes, [PERMISSIONS.TREATMENT_STAFF_DELETE]);

    // Normalize search query: empty string becomes undefined
    const normalizedSearchQuery = searchQuery && searchQuery.trim() ? searchQuery.trim().toLowerCase() : undefined;

    // Build query params object, only including defined values (clinicId only, search will be done client-side)
    const queryParams = useMemo(() => {
        const params: { clinicId?: string } = {};
        if (selectedClinic) {
            params.clinicId = selectedClinic;
        }
        return params;
    }, [selectedClinic]);

    // Fetch staff data - filter by selected clinic if set
    const { data: staffData, isLoading, error } = useStaff(queryParams);

    // Fetch clinics to display names
    const { data: clinicsData } = useClinics({});

    // Fetch all user data for staff members in parallel
    const userQueries = useQueries({
        queries:
            staffData?.data?.map((staff) => ({
                queryKey: ["user", staff.userId],
                queryFn: async () => {
                    const response = await authSvcAxiosInstance.get(authServiceEndpoints.getUserById(staff.userId));
                    return response.data;
                },
                enabled: !!staff.userId,
            })) || [],
    });

    // Create a map of userId -> userData for quick lookup
    const userDataMap = useMemo(() => {
        const map = new Map<string, { name?: string; email?: string }>();
        if (staffData?.data) {
            staffData.data.forEach((staff, index) => {
                const query = userQueries[index];
                if (query?.data) {
                    map.set(staff.userId, {
                        name: query.data.name,
                        email: query.data.email,
                    });
                }
            });
        }
        return map;
    }, [userQueries, staffData?.data]);

    // Filter staff data client-side based on search query
    const filteredStaffData = useMemo(() => {
        if (!staffData?.data || !normalizedSearchQuery) {
            return staffData?.data || [];
        }

        return staffData.data.filter((staff) => {
            const userData = userDataMap.get(staff.userId);
            const name = userData?.name?.toLowerCase() || "";
            const userId = staff.userId.toLowerCase();
            const email = userData?.email?.toLowerCase() || "";

            return (
                name.includes(normalizedSearchQuery) ||
                userId.includes(normalizedSearchQuery) ||
                email.includes(normalizedSearchQuery)
            );
        });
    }, [staffData?.data, normalizedSearchQuery, userDataMap]);

    // Mutations
    const deleteStaffMutation = useDeleteStaff();
    const createStaffMutation = useCreateStaff();
    const updateStaffMutation = useUpdateStaff();

    const handleOpenAddStaffModal = () => {
        setEditingStaff(null);
        setIsStaffModalOpen(true);
    };

    const handleOpenEditStaffModal = (staff: Staff) => {
        setEditingStaff(staff);
        setIsStaffModalOpen(true);
    };

    const handleCloseStaffModal = () => {
        setIsStaffModalOpen(false);
        setEditingStaff(null);
    };

    const handleSubmitStaff = async (data: CreateStaffRequest) => {
        if (editingStaff) {
            await updateStaffMutation.mutateAsync({
                id: editingStaff.id,
                data,
            });
        } else {
            await createStaffMutation.mutateAsync(data);
        }
    };

    const handleDeleteStaff = (staffId: string) => {
        setStaffToDelete(staffId);
        setDeleteDialogOpen(true);
    };

    const confirmDeleteStaff = async () => {
        if (!staffToDelete) return;

        try {
            await deleteStaffMutation.mutateAsync(staffToDelete);
            setDeleteDialogOpen(false);
            setStaffToDelete(null);
        } catch (error) {
            Sentry.captureException(error, {
                tags: {
                    component: "StaffPage",
                    action: "deleteStaff",
                },
            });
        }
    };

    // Update search param in URL when search changes (debounced)
    const handleDebouncedSearchChange = useCallback(
        (value: string) => {
            const trimmedValue = value.trim();
            navigate({
                to: "/staff",
                search: {
                    search: trimmedValue || undefined,
                },
                replace: true, // Replace history entry to avoid cluttering browser history
            });
        },
        [navigate]
    );

    // Sync input value with URL search param when it changes externally
    useEffect(() => {
        setInputValue(searchQuery || "");
    }, [searchQuery]);

    return (
        <div className="h-full overflow-auto p-4">
            <motion.div {...fadeInUp} transition={transitions.slow}>
                <div className="flex justify-end gap-2">
                    <SearchBar
                        value={inputValue}
                        onChange={setInputValue}
                        onDebouncedChange={handleDebouncedSearchChange}
                        debounceMs={300}
                        placeholder={t("search_staff")}
                        className="w-96 md:w-[300px]"
                    />
                    {canCreate && (
                        <Button variant="default" onClick={handleOpenAddStaffModal}>
                            <Icon icon={TbPlus} />
                            {t("add_staff")}
                        </Button>
                    )}
                </div>
                <div className="rounded-md">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>{t("name")}</TableHead>
                                <TableHead>{t("user_id")}</TableHead>
                                <TableHead>{t("clinics")}</TableHead>
                                <TableHead>{t("actions")}</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {isLoading ? (
                                <TableRow>
                                    <TableCell className="text-center py-8">{t("loading")}...</TableCell>
                                </TableRow>
                            ) : error ? (
                                <TableRow>
                                    <TableCell className="text-center py-8 text-red-500">
                                        {t("error_loading_staff")}
                                    </TableCell>
                                </TableRow>
                            ) : !filteredStaffData.length ? (
                                <TableRow>
                                    <TableCell className="text-center py-8">{t("no_staff_found")}</TableCell>
                                </TableRow>
                            ) : (
                                filteredStaffData.map((staff) => (
                                    <StaffRow
                                        key={staff.id}
                                        staff={staff}
                                        clinicsData={clinicsData?.data || []}
                                        onEdit={() => handleOpenEditStaffModal(staff)}
                                        onDelete={() => handleDeleteStaff(staff.id)}
                                        isDeleting={deleteStaffMutation.isPending}
                                        canEdit={canEdit}
                                        canDelete={canDelete}
                                    />
                                ))
                            )}
                        </TableBody>
                    </Table>
                </div>
            </motion.div>
            <StaffModal
                isOpen={isStaffModalOpen}
                onClose={handleCloseStaffModal}
                staff={editingStaff}
                onSubmit={handleSubmitStaff}
            />
            <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t("confirm_delete")}</AlertDialogTitle>
                        <AlertDialogDescription>{t("this_action_cannot_be_undone")}</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={deleteStaffMutation.isPending}>{t("cancel")}</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={confirmDeleteStaff}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            disabled={deleteStaffMutation.isPending}
                        >
                            {deleteStaffMutation.isPending ? t("deleting") : t("delete")}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}

export const Route = createFileRoute("/staff")({
    component: StaffPage,
    validateSearch: (search) => staffSearchSchema.parse(search),
});
